import React, { ChangeEvent, useEffect, useState } from 'react';
import { PlansENUM, UserItemProps, InfluencerLink } from '../index';
import { Form } from '../../../../components';
import {
  InstructionWrapper,
  TextField,
  ToggleSwitch
} from '../../../../components/fields';
import Heading from '../../../../_global/components/heading/Heading';
import './_styles.scss';
import { publish, useStateReducer } from '../../../../hooks';
import { validateObject } from '../../../../utilities';
import { Regexes } from '../../../../definitions';
import { StateTopicEnum } from '../../../../enums';
import {
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import { roleDataService } from '../../../roles/services';
import { UserPermissions } from '../../../../../src/definitions';
import { securityService } from '../../../../../src/_global/services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Reorder } from 'framer-motion';
import { faRemove } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import config from "../../../../_config/global";

export default function CreateItem({
  data: { email_address, first_name, last_name, source, awayz_access, userRegion }
}: UserItemProps) {
  const [state, setState] = useStateReducer({
    email_address: '',
    first_name,
    last_name,
    source,
    admin_right: false,
    awayz_access: {
      awayz_forever_enum: PlansENUM.free
    },
    has_awayz_access: awayz_access
      ? awayz_access.awayz_forever_enum != PlansENUM.free
      : false,
    password: '',
    user_roles: [],
    influencer_links: [],
    emailVerified: true,
    userRegion : config.defaults.user_region,
  });
  const [userRegions, setUserRegions] = useState<any>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>(config.defaults.user_region);
  const AWAYZ_PWD = 'Awayz01@';
  const can_edit_awayz_access = securityService.checkRoleAccess(
    UserPermissions.editUserAwayzAccess
  );
  const [displayName, setDisplayName] = React.useState(
    `${first_name ?? ''} ${last_name ? last_name + ' | ' : ''} ${
      email_address ?? ''
    }
`.trim()
  );

  const errors = validateObject(
    {
      first_name: {
        regex: Regexes.anyContentRegex,
        message: 'First and Last Name are required.'
      },
      last_name: {
        regex: Regexes.anyContentRegex,
        message: 'First and Last Name are required.'
      },
      email_address: {
        regex: Regexes.emailRegex,
        message: 'Please enter a valid email address.'
      },
      password: {
        regex: Regexes.anyContentRegex,
        message: 'Please enter a password.'
      },
      userRegion: {
        regex : Regexes.anyContentRegex,
        message : 'Please select a region'
      }
    },
    state
  );

  const isValidInfluencerLinks = () => {
    let tisValid = true;
    state.influencer_links &&
      state.influencer_links.forEach((element: InfluencerLink) => {
        if (element.code === '' || element.url === '') tisValid = false;
      });
    return tisValid;
  };

  const isValid = () => {
    if (errors['email_address']) return false;
    if (errors['first_name']) return false;
    if (errors['last_name']) return false;
    if (errors['userRegion']) return false;
    if (can_edit_awayz_access && state.admin_right && errors['password'])
      return false;
    if (!isValidInfluencerLinks()) return false;
    return true;
  };

  React.useEffect(() => {
    setDisplayName(
      `${state.first_name ?? ''} ${
        state.last_name ? state.last_name + ' | ' : ''
      } ${state.email_address ?? ''}
    `.trim()
    );
    publish(StateTopicEnum.IsValid, isValid());
    publish(StateTopicEnum.EditItem, state);
    // eslint-disable-next-line
  }, [
    state.email_address,
    state.first_name,
    state.last_name,
    state.admin_right,
    state.source,
    state.awayz_access,
    state.has_awayz_access,
    state.password,
    state.user_roles,
    state.influencer_links,
    state.userRegion
  ]);

  useEffect(() => {
    setState({ password: !state.admin_right ? AWAYZ_PWD : '' });
  }, [state.admin_right]);

  useEffect(() => {
    fetchRoleData();
  }, [userRegions]);

  useEffect(() => {
    const fetchUserRegionData = async () => {
      try {
        const fetchedUserRegions = await (await fetch(`${config.URL}${config.endpoints.regions.getRegions}`)).json();
        
        if (fetchedUserRegions) {
          setUserRegions(fetchedUserRegions.userRegions);
        }
      } catch (error) {
        console.error("Error fetching user regions:", error);
      }
    };
  
    fetchUserRegionData();
  }, []);

  const handleRegionChange = async (event : string) =>{
    setSelectedRegion(event);
    setState({
      userRegion: event,
    });
    publish(StateTopicEnum.EditItem, state);
    publish(StateTopicEnum.IsValid, isValid());
  }

  const fetchRoleData = async () => {
    const rolesData = await roleDataService.getRoles(true);
    setState({ roles: rolesData });
  };

  const handleState = (key: string, value: any) => {
    setState({ ...state, [key]: value });

    publish(StateTopicEnum.EditItem, state);
    publish(StateTopicEnum.IsValid, isValid());
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    let value: string | boolean = event.currentTarget.value;
    // @ts-ignore
    if (event.target.type === 'checkbox') value = event.target.checked;
    setState({ [key]: value });
  };

  const handleRole = (checked: boolean, value: string) => {
    let selectedArray: string[] = state.user_roles ? state.user_roles : [];

    if (checked) {
      if (!selectedArray.includes(value)) selectedArray.push(value);
    } else {
      selectedArray = selectedArray.filter((item) => item !== value);
    }
    setState({ user_roles: selectedArray });
  };

  const handleAwayzAccess = (event: React.ChangeEvent<HTMLInputElement>) => {
    let mapAccess: any = {
      active: false,
      awayz_forever_enum: PlansENUM.free
    };

    let value: string | boolean = (event.target as HTMLInputElement).value;

    switch (value) {
      case PlansENUM.gold:
        mapAccess.active = true;
        mapAccess.awayz_forever_enum = PlansENUM.gold;
        break;
      case PlansENUM.platinum:
        mapAccess.active = true;
        mapAccess.awayz_forever_enum = PlansENUM.platinum;
        break;
    }
    setState({ has_awayz_access: value != PlansENUM.free });
    setState({ awayz_access: mapAccess });
  };

  return (
    <div id='user-create-item'>
      <Heading name={displayName} />
      <Form title='Personal Details'>
        <InstructionWrapper
          text="Enter the person's first and last name here."
          error={errors['first_name'] || errors['last_name']}
          row
        >
          <TextField
            label='First Name'
            value={state.first_name}
            onChange={(event) => handleChange(event, 'first_name')}
            error={!!errors['first_name']}
          />
          <TextField
            label='Last Name'
            value={state.last_name}
            onChange={(event) => handleChange(event, 'last_name')}
            error={!!errors['last_name']}
          />
        </InstructionWrapper>
        <InstructionWrapper
          text="Enter the person's email address and source here."
          error={errors['email_address']}
          row
        >
          <TextField
            label='Email Address'
            value={state.email_address}
            onChange={(event) => handleChange(event, 'email_address')}
            error={!!errors['email_address']}
          />
          {securityService.checkRoleAccess(UserPermissions.editUserSource) ? (
            <TextField
              label='Source'
              value={state.source}
              onChange={(event) => handleChange(event, 'source')}
            />
          ) : (
            <p></p>
          )}
        </InstructionWrapper>
      </Form>

      {securityService.checkRoleAccess(UserPermissions.editUserAwayzAccess) ? (
        <Form title='Environment Access'>
          <InstructionWrapper text={'Admin Portal access'} row>
            <FormControl>
              <FormLabel id='admin-portal-environment-access-label'>
                Admin Portal
              </FormLabel>
              <InstructionWrapper row>
                <ToggleSwitch
                  label={state.admin_right ? 'Yes' : 'No'}
                  className={'display-block'}
                  name={'awayz_access'}
                  value={state.admin_right}
                  onChange={(event) => handleChange(event, 'admin_right')}
                />
                <TextField
                  label={'Password'}
                  value={state.password}
                  onChange={(event) => handleChange(event, 'password')}
                  readOnly={!state.admin_right}
                  error={!!errors['password']}
                />
              </InstructionWrapper>
            </FormControl>
          </InstructionWrapper>
          <br />

          <InstructionWrapper
            text={
              "'Manual Access' to override 'Stripe Access' \n Reason: demote or promote user "
            }
            row
          >
            <FormControl>
              <FormLabel id='awayz-environment-access-label'>
                Awayz.com
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby='awayz-environment-access-label'
                defaultValue={PlansENUM.free}
                name='awayz-access-radio-buttons-group'
                onChange={handleAwayzAccess}
              >
                <FormControlLabel
                  value={PlansENUM.free}
                  control={<Radio />}
                  label='None'
                />
                <FormControlLabel
                  value={PlansENUM.gold}
                  control={<Radio />}
                  label='Awayz Gold'
                />
                <FormControlLabel
                  value={PlansENUM.platinum}
                  control={<Radio />}
                  label='Awayz Platinum'
                />
              </RadioGroup>
            </FormControl>
          </InstructionWrapper>
        </Form>
      ) : null}

      <Form title='User Region'>
        <InstructionWrapper text={'Select the user region'} row>
          <FormControl>
            <Select
              id="user_region"
              value={selectedRegion ? {label : selectedRegion} : {label : 'Please select a region'}}
              placeholder="Please select a region"
              onChange={async (selectedOption : any) => await handleRegionChange(selectedOption.value)}
              options={userRegions && userRegions.length > 0 ? userRegions.map((userRegion : any) => ({ label: userRegion.name, value: userRegion.code })) : [{ label: "EMPTY", value: "EMPTY" }]}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  padding: '0.5rem',
                  fontSize: '1rem',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  color: 'white !important',
                  backgroundColor: '#0000001A',
                  margin: '12px 0px'
                }),
                option : (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected ? '#0000003A' : 'transparent',
                  color: state.isSelected ? 'white' : 'black',
                  ':hover': {
                    backgroundColor: '#0000003A',
                    color: 'white',
                  },
                }),
                singleValue: (provided, state) => ({
                  ...provided,
                  color: 'white',
                })
              }}
              
            />
          </FormControl>
        </InstructionWrapper>
      </Form>

      <Form>
        <Heading
          style={{ margin: '12px 0px' }}
          name='Influencer Links'
          onClickRight={() => {
            if (!state.influencer_links) {
              state.influencer_links = [];
            }

            let lastLink: InfluencerLink = state.influencer_links.at(-1);

            if (!lastLink || (lastLink.code && lastLink.url)) {
              let newLink: InfluencerLink = {
                code: '',
                url: ''
              };
              state.influencer_links.push(newLink);
              handleState('influencer_links', state.influencer_links);
            }
          }}
        />

        <Reorder.Group
          as='span'
          axis='y'
          className='program-card-container'
          values={state.influencer_links}
          onReorder={(newOrder: any[]) => {
            let aux = { ...state.influencer_links };
            aux = [state.influencer_links[0], ...newOrder];
            handleState('influencer_links', aux);
          }}
        >
          {state.influencer_links.map(
            (influencer_link: InfluencerLink, index: number) => {
              let child;
              child = (
                <Card
                  key={'influencer_link_' + index}
                  className='influencer-link-card-create page-padding'
                >
                  <FontAwesomeIcon
                    className={'remove-program'}
                    icon={faRemove}
                    onClick={() => {
                      if (index > -1) {
                        state.influencer_links.splice(index, 1);
                      }
                      handleState('influencer_links', state.influencer_links);
                    }}
                  />
                  <TextField
                    value={index + 1}
                    style={{ width: '40px' }}
                    readOnly
                    error={errors['InfluencerLinks'] ? true : false}
                  ></TextField>
                  <TextField
                    className='next-each-other'
                    label={'Code'}
                    value={influencer_link.code}
                    error={influencer_link.code === ''}
                    onChange={(event) => {
                      state.influencer_links[index].code =
                        event.currentTarget.value;
                      handleState('influencer_links', state.influencer_links);
                    }}
                  ></TextField>

                  <TextField
                    className='next-each-other'
                    label={'Url'}
                    value={influencer_link.url}
                    error={influencer_link.url === ''}
                    onChange={(event) => {
                      state.influencer_links[index].url =
                        event.currentTarget.value;
                      handleState('influencer_links', state.influencer_links);
                    }}
                  ></TextField>
                </Card>
              );

              return (
                <Reorder.Item
                  key={`reorder_${index}`}
                  value={influencer_link}
                  drag={false}
                  draggable={false}
                >
                  {child}
                </Reorder.Item>
              );
            }
          )}
        </Reorder.Group>
      </Form>

      <Form>
        <Heading name='Account Roles'></Heading>
        {state.roles?.map((role: any, index: number) => {
          return (
            <div key={'user_roles_card_' + index}>
              <InstructionWrapper row text={role.rolename + ' Role'}>
                <Card className='program-card-edit page-padding'>
                  {
                    <div key={role.rolename + index} className='flex-row'>
                      <div className='label-col'>
                        <TextField value={role.rolename} readOnly></TextField>
                      </div>

                      <div className='switch-col'>
                        <ToggleSwitch
                          label=''
                          name={role._id}
                          value={state.user_roles.includes(role._id)}
                          onChange={(event) =>
                            handleRole(event.target.checked, role._id)
                          }
                          readOnly={!role.is_active}
                        />
                      </div>
                    </div>
                  }
                </Card>
              </InstructionWrapper>
            </div>
          );
        })}
      </Form>
    </div>
  );
}
